import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Icon from "@ats/src/components/shared/Icon";
import Accordion, { AccordionSection, AccordionRow } from "@ats/src/components/shared/Accordion";

export type CriterionScore = "full_match" | "partial_match" | "not_found";
export type CriterionTier = "tier_1" | "tier_2" | "tier_3";

export interface CriterionResult {
  tier: CriterionTier;
  score: CriterionScore;
  criterionText: string;
  reasoning: string;
}

interface Props {
  criteria: CriterionResult[];
  startOpen?: boolean;
}

const SCORE_RANK: Record<CriterionScore, number> = {
  full_match: 0,
  partial_match: 1,
  not_found: 2,
};

const TIERS: { key: CriterionTier; label: string }[] = [
  { key: "tier_1", label: "Core criteria" },
  { key: "tier_2", label: "Preferred criteria" },
  { key: "tier_3", label: "Bonus criteria" },
];

const ScoreMark: React.FC<{ score: CriterionScore }> = ({ score }) => {
  if (score === "full_match") {
    return (
      <Styled.FullMark viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="11" />
        <path d="M7.5 12.5l3 3 6-7" />
      </Styled.FullMark>
    );
  }
  if (score === "partial_match") {
    return (
      <Styled.PartialMark>
        <Icon name="minus-circle" />
      </Styled.PartialMark>
    );
  }
  return (
    <Styled.NotFoundMark>
      <Icon name="x-circle" />
    </Styled.NotFoundMark>
  );
};

/* Tally — header meta: met · partial · missing */
const Tally: React.FC<{ counts: Record<CriterionScore, number> }> = ({ counts }) => (
  <Styled.Tally>
    {(["full_match", "partial_match", "not_found"] as CriterionScore[]).map((s) => (
      <Styled.TallyItem key={s}>
        <ScoreMark score={s} />
        {counts[s]}
      </Styled.TallyItem>
    ))}
  </Styled.Tally>
);

function ScoringDetail({ criteria, startOpen = false }: Props) {
  const counts: Record<CriterionScore, number> = { full_match: 0, partial_match: 0, not_found: 0 };
  criteria.forEach((c) => {
    counts[c.score] += 1;
  });

  const byRank = (a: CriterionResult, b: CriterionResult) => SCORE_RANK[a.score] - SCORE_RANK[b.score];

  const sections = TIERS.map((tier) => ({
    ...tier,
    items: criteria.filter((c) => c.tier === tier.key).sort(byRank),
  })).filter((tier) => tier.items.length > 0);

  return (
    <Accordion
      title="Scoring detail"
      count={`${criteria.length} criteria`}
      meta={<Tally counts={counts} />}
      defaultOpen={startOpen}
    >
      {sections.map((tier) => (
        <AccordionSection key={tier.key} label={tier.label} count={tier.items.length}>
          {tier.items.map((c, i) => {
            const isNotFound = c.score === "not_found";
            return (
              <AccordionRow
                key={i}
                first={i === 0}
                leading={<ScoreMark score={c.score} />}
                label={isNotFound ? <Styled.NotFoundText>{c.criterionText}</Styled.NotFoundText> : c.criterionText}
              >
                {c.reasoning}
              </AccordionRow>
            );
          })}
        </AccordionSection>
      ))}
    </Accordion>
  );
}

export default ScoringDetail;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.FullMark = styled.svg((props: any) => {
  const t: any = props.theme;
  return css`
    label: ScoringDetail_FullMark;
    display: block;
    flex-shrink: 0;
    width: 17.5px;
    height: 17.5px;

    circle {
      fill: ${t.dark ? t.color.gray[100] : t.color.black};
    }

    path {
      fill: none;
      stroke: ${t.dark ? t.color.gray[900] : t.color.white};
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  `;
});

Styled.PartialMark = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: ScoringDetail_PartialMark;
    display: inline-flex;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[700]};
  `;
});

Styled.NotFoundMark = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: ScoringDetail_NotFoundMark;
    display: inline-flex;
    color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
  `;
});

Styled.NotFoundText = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: ScoringDetail_NotFoundText;
    color: ${t.dark ? t.color.gray[600] : t.color.gray[400]};
  `;
});

Styled.Tally = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: ScoringDetail_Tally;
    display: inline-flex;
    gap: ${t.spacing[3]};
    color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};
    font-variant-numeric: tabular-nums;
  `;
});

Styled.TallyItem = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: ScoringDetail_TallyItem;
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[1]};
    ${t.text.sm}
  `;
});
